/**
 * speech-sound-meter.js — ПРИБОР для постановки звуков речи.
 *
 * Зачем прибор там, где обычно стоит игра.
 *
 * Приложения для занятий с логопедом устроены одинаково: картинка, запись,
 * звёздочка. Оценивает результат либо родитель («молодец!»), либо
 * распознавание речи, которое отвечает «слово услышано» — а услышать «лыба»
 * вместо «рыба» оно может с тем же успехом. Ребёнок закрепляет неправильный
 * звук, и месяцы занятий уходят в закрепление дефекта.
 *
 * Здесь измеряется АКУСТИКА, и измеряется то, что описано в клинической
 * фонетике и различает норму от дефекта:
 *
 *   Р — альвеолярный дрожащий несёт информацию в АМПЛИТУДНОЙ МОДУЛЯЦИИ на
 *       20–40 Гц; нормальный вариант содержит два смыкания языка (разброс
 *       от нуля до пяти). Одноударное «р», горловое и замена на «л» дают
 *       другую картину модуляции — и это видно в цифрах, а не на слух
 *       родителя.
 *
 *   С / Ш — различаются положением спектрального пика; в исследованиях
 *       детской речи пик в полосе 3–9 кГц предсказывает оценку слушателя
 *       ЛУЧШЕ, чем классические спектральные моменты. Межзубный сигматизм
 *       сдвигает пик вниз, к области «ш», при более слабой энергии.
 *
 * ⚠️ ЧЕГО ЭТОТ МОДУЛЬ НЕ ДЕЛАЕТ: он не ставит диагноз и не заменяет
 * логопеда. Он измеряет и показывает измеренное — как линейка, а не как
 * врач. Разница принципиальна и вынесена в тексты инструмента.
 */
(function () {
    'use strict';

    function fft() {
        var F = window.DspFft;
        if (!F) throw new Error('DspFftMissing');
        return F;
    }

    function nextPow2(n) { var p = 1; while (p < n) p <<= 1; return p; }

    /* ── огибающая амплитуды ─────────────────────────────────────────── */
    /**
     * Выпрямление плюс однополюсный сглаживатель. Гильберта здесь не нужно:
     * дрожание — это модуляция на 20–40 Гц, и полоса сглаживания 150 Гц
     * пропускает её целиком, отбрасывая несущую.
     */
    function envelope(pcm, sr, cutHz) {
        var a = Math.exp(-2 * Math.PI * (cutHz || 150) / sr);
        var out = new Float32Array(pcm.length);
        var y = 0;
        for (var i = 0; i < pcm.length; i++) {
            var x = Math.abs(pcm[i]);
            y = (1 - a) * x + a * y;
            out[i] = y;
        }
        return out;
    }

    /* ── энергия по кадрам ───────────────────────────────────────────── */
    function frameRms(pcm, sr, frameMs, hopMs) {
        var fl = Math.round(sr * frameMs / 1000), hl = Math.round(sr * hopMs / 1000);
        var n = Math.max(0, Math.floor((pcm.length - fl) / hl) + 1);
        var out = new Float64Array(n);
        for (var f = 0; f < n; f++) {
            var off = f * hl, s = 0;
            for (var i = 0; i < fl; i++) { var v = pcm[off + i]; s += v * v; }
            out[f] = Math.sqrt(s / fl);
        }
        return { rms: out, frameLen: fl, hopLen: hl };
    }

    /** Границы речи: от первого до последнего кадра выше порога. */
    function speechSpan(pcm, sr) {
        var fr = frameRms(pcm, sr, 20, 10);
        var sorted = Array.prototype.slice.call(fr.rms).sort(function (a, b) { return a - b; });
        if (!sorted.length) return null;
        var floor = sorted[Math.floor(sorted.length * 0.1)];
        var peak = sorted[Math.floor(sorted.length * 0.95)];
        /* ⚠️ Порог «в три раза выше пола» ломается на сигнале БЕЗ тишины:
           у ровного шума десятый и девяносто пятый перцентили почти равны,
           floor*3 оказывается выше самого сигнала, и речь «не найдена».
           Стенд поймал это сразу на четырёх полосовых шумах подряд. Если
           динамики нет — значит звучит вся запись, и делить нечего. */
        var thr = (peak > floor * 4) ? Math.max(floor * 3, peak * 0.10) : peak * 0.25;
        var first = -1, last = -1;
        for (var i = 0; i < fr.rms.length; i++) {
            if (fr.rms[i] >= thr) { if (first < 0) first = i; last = i; }
        }
        if (first < 0) return null;
        return {
            from: first * fr.hopLen,
            to: Math.min(pcm.length, last * fr.hopLen + fr.frameLen),
            thr: thr, peak: peak, fr: fr
        };
    }

    /* ── спектр участка ──────────────────────────────────────────────── */
    function spectrum(pcm, sr, from, to) {
        var F = fft();
        var len = Math.min(to - from, Math.round(sr * 0.05));
        if (len < 128) return null;
        var size = nextPow2(len);
        var win = F.hann(size), tw = F.makeTwiddles(size);
        var bins = (size >> 1) + 1;
        var acc = new Float64Array(bins);
        var re = new Float64Array(size), im = new Float64Array(size);
        var hop = Math.max(1, Math.round(size / 2)), frames = 0;
        for (var off = from; off + size <= to; off += hop) {
            for (var i = 0; i < size; i++) { re[i] = pcm[off + i] * win[i]; im[i] = 0; }
            F.fft(re, im, tw);
            for (var k = 0; k < bins; k++) acc[k] += re[k] * re[k] + im[k] * im[k];
            frames++;
        }
        if (!frames) {
            for (var j = 0; j < size; j++) { re[j] = (from + j < to ? pcm[from + j] : 0) * win[j]; im[j] = 0; }
            F.fft(re, im, tw);
            for (var m = 0; m < bins; m++) acc[m] = re[m] * re[m] + im[m] * im[m];
            frames = 1;
        }
        return { mag: acc, size: size, bins: bins, sr: sr };
    }

    function peakInBand(sp, loHz, hiHz) {
        var k0 = Math.max(1, Math.round(loHz * sp.size / sp.sr));
        var k1 = Math.min(sp.bins - 1, Math.round(hiHz * sp.size / sp.sr));
        var best = -1, bi = k0;
        for (var k = k0; k <= k1; k++) if (sp.mag[k] > best) { best = sp.mag[k]; bi = k; }
        return { hz: bi * sp.sr / sp.size, power: best };
    }

    function centroidInBand(sp, loHz, hiHz) {
        var k0 = Math.max(1, Math.round(loHz * sp.size / sp.sr));
        var k1 = Math.min(sp.bins - 1, Math.round(hiHz * sp.size / sp.sr));
        var num = 0, den = 0;
        for (var k = k0; k <= k1; k++) { var f = k * sp.sr / sp.size; num += f * sp.mag[k]; den += sp.mag[k]; }
        return den > 0 ? num / den : 0;
    }

    function bandEnergy(sp, loHz, hiHz) {
        var k0 = Math.max(1, Math.round(loHz * sp.size / sp.sr));
        var k1 = Math.min(sp.bins - 1, Math.round(hiHz * sp.size / sp.sr));
        var s = 0;
        for (var k = k0; k <= k1; k++) s += sp.mag[k];
        return s;
    }

    /* ── 1. Свистящие и шипящие ──────────────────────────────────────── */

    /**
     * Ищет самый длинный шумный участок и измеряет положение его пика.
     *
     * Шумный участок узнаётся не по громкости, а по ОТНОШЕНИЮ энергии выше
     * 2.5 кГц к энергии ниже: у гласного оно мало, у «с» и «ш» велико. Это
     * работает и на тихой записи, и на громкой, и не требует распознавания
     * речи — что важно, потому что распознать «неправильный» звук у ребёнка
     * как раз и не удаётся.
     */
    function sibilant(pcm, sr) {
        var span = speechSpan(pcm, sr);
        if (!span) return { found: false, reason: 'no_speech' };

        var frameMs = 20, hopMs = 10;
        var fl = Math.round(sr * frameMs / 1000), hl = Math.round(sr * hopMs / 1000);
        var marks = [];
        for (var off = span.from; off + fl <= span.to; off += hl) {
            var sp = spectrum(pcm, sr, off, off + fl);
            if (!sp) { marks.push(0); continue; }
            var hi = bandEnergy(sp, 2500, Math.min(10000, sr / 2 - 200));
            var lo = bandEnergy(sp, 150, 2500);
            marks.push(hi / Math.max(1e-12, hi + lo));
        }
        /* Порог 0.55: у гласных доля верхней полосы обычно ниже 0.2, у
           свистящих и шипящих выше 0.7. Промежуток намеренно широкий — на
           краю фрикатива всегда есть переходные кадры. */
        /* ВСЕ шумные участки, а не только самый длинный.
           «Шесть» содержит и «ш» в начале, и «сть» в конце; самый длинный
           там второй, и прибор уверенно измерял НЕ ТОТ звук — стенд показал
           пик 6117 Гц и вердикт «с» на слове, начинающемся с «ш». Слово с
           двумя фрикативами не редкость, поэтому выбирать за пользователя
           тут нельзя: возвращаются все, а тул показывает их на дорожке. */
        var runs = [], cur = { len: 0, start: -1 };
        for (var i = 0; i <= marks.length; i++) {
            var on = i < marks.length && marks[i] >= 0.55;
            if (on) { if (cur.len === 0) cur.start = i; cur.len++; }
            else if (cur.len >= 3) { runs.push({ start: cur.start, len: cur.len }); cur = { len: 0, start: -1 }; }
            else { cur = { len: 0, start: -1 }; }
        }
        if (!runs.length) return { found: false, reason: 'no_fricative' };

        var top = Math.min(9000, sr / 2 - 200);
        var segments = runs.map(function (r) {
            var f0 = span.from + r.start * hl;
            var t0 = Math.min(span.to, f0 + r.len * hl + fl);
            var spr = spectrum(pcm, sr, f0, t0);
            if (!spr) return null;
            var p = peakInBand(spr, 2000, top);
            return {
                from: f0, to: t0, atSec: f0 / sr,
                durMs: Math.round((t0 - f0) / sr * 1000),
                peakHz: Math.round(p.hz),
                centroidHz: Math.round(centroidInBand(spr, 2000, top)),
                verdict: p.hz < 4200 ? 'sh' : (p.hz > 5600 ? 's' : 'between')
            };
        }).filter(Boolean);
        if (!segments.length) return { found: false, reason: 'too_short' };

        var longest = segments[0];
        segments.forEach(function (g) { if (g.durMs > longest.durMs) longest = g; });
        var from = longest.from, to = longest.to;
        var pk = { hz: longest.peakHz };
        var cen = longest.centroidHz;
        var durMs = longest.durMs;

        /* Граница «ш / с» на 5 кГц: в детской речи пик «ш» лежит около
           3–4.5 кГц, «с» — около 6–9 кГц. Промежуточное положение — это
           либо смягчённый вариант, либо межзубный сигматизм, и различить
           их по одному пику нельзя; инструмент так и говорит. */
        var verdict;
        if (pk.hz < 4200) verdict = 'sh';
        else if (pk.hz > 5600) verdict = 's';
        else verdict = 'between';

        return {
            found: true, peakHz: Math.round(pk.hz), centroidHz: Math.round(cen),
            durMs: Math.round(durMs), verdict: verdict,
            from: from, to: to, segments: segments
        };
    }

    /* ── 2. Дрожащий Р ───────────────────────────────────────────────── */

    /**
     * Считает СМЫКАНИЯ языка по провалам огибающей и оценивает их темп.
     *
     * Почему провалы, а не спектр: дрожащий отличается от всех остальных
     * звуков именно тем, что язык периодически перекрывает поток, и в
     * огибающей появляются регулярные ямы на 20–40 Гц. У «л», у горлового
     * «р» и у пропуска звука такой картины нет — и это видно числом, а не
     * ощущением.
     */
    function trill(pcm, sr) {
        var span = speechSpan(pcm, sr);
        if (!span) return { found: false, reason: 'no_speech' };

        /* Полоса сглаживания 60 Гц, а НЕ 150. При 150 Гц сквозь огибающую
           проходит рябь основного тона (у детей 200–300 Гц, у взрослых
           100–130), и подсчёт смыканий насчитывал шесть ударов там, где их
           было два: он считал периоды голоса, а не движения языка. */
        var env = envelope(pcm, sr, 60);

        /* ⚠️ Из огибающей ОБЯЗАТЕЛЬНО вычитается медленный тренд.
           Без этого «глубина модуляции» меряет не дрожание, а слог: в любом
           окне 130 мс есть начало и конец звука, максимум берётся с гласного,
           минимум — с тишины, и глубина выходит 0.9 у чего угодно. Стенд
           показал это в чистом виде: ровный гласный получил вердикт «дрожащий»
           с глубиной 1.00, и все семь настоящих слов вместе с ним.
           Тренд — та же огибающая с полосой 8 Гц (это темп слогов), а
           дрожание живёт на 18–45 Гц и целиком остаётся в остатке. */
        var slow = envelope(env, sr, 8);
        /* Знаменатель ЗАЖАТ снизу. Относительная модуляция (env−slow)/slow
           при slow→0 улетает в десятки: в паузе делитель почти нулевой, и
           «глубина» получалась 21.6 у ровного гласного. Пол берётся от пика
           записи, а не от абсолютной величины — записи бывают тихие. */
        var floorLvl = Math.max(1e-6, span.peak * 0.05);
        var mod = new Float32Array(env.length);
        for (var mi = 0; mi < env.length; mi++) {
            mod[mi] = (env[mi] - slow[mi]) / Math.max(floorLvl, slow[mi]);
        }

        /* ГОЛОСОВОЙ фильтр: дрожащий — звонкий звук, язык перекрывает поток
           голоса. Шипящие и свистящие тоже дают колебания огибающей (шум
           случаен и коррелирует на случайных задержках), и без этой проверки
           прибор объявлял дрожание в словах «шесть», «семь» и «девять», где
           звука «р» нет вовсе. Признак звонкости — перевес энергии ниже
           1 кГц над энергией выше 3 кГц. */
        function voicedAt(off, len) {
            var sp = spectrum(pcm, sr, off, off + len);
            if (!sp) return false;
            var lo = bandEnergy(sp, 80, 1000);
            var hi = bandEnergy(sp, 3000, Math.min(9000, sr / 2 - 200));
            return lo > hi * 2;
        }

        /* АВТОКОРРЕЛЯЦИЯ огибающей, а не её БПФ.
           Дрожание длится 60–150 мс — две-четыре периода. Спектр такого
           отрезка размазан: измеренная «доля энергии в полосе» прыгала от
           0.07 до 0.20 на сигналах с ЗАВЕДОМО одинаковой модуляцией, и
           вердикт получался лотереей. Автокорреляция отвечает на нужный
           вопрос прямо: «повторяется ли огибающая с периодом 22–55 мс и
           насколько похоже». */
        var minLag = Math.round(sr / 45), maxLag = Math.round(sr / 18);
        var win = Math.round(sr * 0.13);
        var hop = Math.round(sr * 0.010);
        var best = null;

        for (var off = span.from; off + win <= span.to; off += hop) {
            var loud = 0, i;
            for (i = 0; i < win; i++) loud += env[off + i];
            loud /= win;
            if (loud < span.peak * 0.12) continue;
            if (!voicedAt(off, win)) continue;

            /* Глубина — СКО относительной модуляции. У ровного гласного она
               около нуля, у дрожащего язык перекрывает поток и остаток
               колеблется на десятки процентов. */
            var e0 = 0, mean = 0;
            for (i = 0; i < win; i++) mean += mod[off + i];
            mean /= win;
            for (i = 0; i < win; i++) { var d0 = mod[off + i] - mean; e0 += d0 * d0; }
            var depth = Math.sqrt(e0 / win);
            if (depth < 0.08) continue;
            if (e0 < 1e-12) continue;

            var bestR = 0, bestLag = 0;
            for (var lag = minLag; lag <= maxLag && lag < win; lag++) {
                var num = 0, den = 0;
                for (i = 0; i + lag < win; i++) {
                    var a = mod[off + i] - mean, b = mod[off + i + lag] - mean;
                    num += a * b; den += b * b;
                }
                var r = num / Math.sqrt(Math.max(1e-12, e0 * den));
                if (r > bestR) { bestR = r; bestLag = lag; }
            }
            if (!bestLag) continue;
            var score = bestR * depth;
            if (!best || score > best.score) {
                best = { off: off, lag: bestLag, r: bestR, depth: depth, score: score, mean: loud };
            }
        }
        if (!best) return { found: false, reason: 'no_modulation' };

        var rateHz = sr / best.lag;

        /* Длительность дрожащего — сколько подряд идущих окон удержали ту же
           периодичность. Число смыканий выводится из темпа и длительности:
           считать пики огибающей поштучно оказалось ненадёжно (переходы на
           входе и выходе звука давали лишние один-два), а произведение
           темпа на длительность устойчиво и честно округляется. */
        var held = 0;
        for (var o2 = best.off; o2 + win <= span.to; o2 += hop) {
            var m2 = 0, j;
            for (j = 0; j < win; j++) m2 += mod[o2 + j];
            m2 /= win;
            var e2 = 0;
            for (j = 0; j < win; j++) { var dd = mod[o2 + j] - m2; e2 += dd * dd; }
            if (Math.sqrt(e2 / win) < 0.08) break;
            held++;
        }
        var durSec = (held * hop + win) / sr;
        var contacts = Math.max(1, Math.round(rateHz * Math.min(durSec, 0.30)));

        /* Норма русского «р» — периодичность в полосе дрожания при заметной
           глубине. Одноударное отличается длительностью, а не темпом. */
        var verdict;
        if (best.r >= 0.45 && best.depth >= 0.22 && contacts >= 2) verdict = 'trill';
        else if (best.r >= 0.40 && best.depth >= 0.12) verdict = 'single_tap';
        else verdict = 'no_trill';

        return {
            found: true,
            rateHz: Math.round(rateHz * 10) / 10,
            contacts: contacts,
            periodicity: Math.round(best.r * 100) / 100,
            depth: Math.round(best.depth * 100) / 100,
            durMs: Math.round(durSec * 1000),
            verdict: verdict,
            at: best.off / sr
        };
    }

    /* ── 3. Живой режим: один короткий кадр ──────────────────────────── */

    /**
     * Разбор ОДНОГО кадра для живой обратной связи.
     *
     * Это и есть главное в инструменте. Исследования визуальной обратной
     * связи (акустической и ультразвуковой) — единственное направление в
     * коррекции стойких дефектов произношения с рандомизированными
     * испытаниями за спиной. Ультразвук требует прибора у логопеда;
     * акустическая обратная связь требует микрофона, и всё.
     *
     * Ребёнок тянет «ссссс» и ВИДИТ, куда попадает пик его звука. Это
     * замыкает петлю, которой у него нет: своё произношение изнутри головы
     * слышится не так, как снаружи, и «скажи правильно» ничего не значит,
     * пока не показано, что такое правильно.
     *
     * @returns {{voiced:boolean, fricative:boolean, peakHz:number, level:number}}
     */
    function frame(pcm, sr) {
        var n = pcm.length;
        if (n < 256) return null;
        var rms = 0, i;
        for (i = 0; i < n; i++) rms += pcm[i] * pcm[i];
        rms = Math.sqrt(rms / n);
        var sp = spectrum(pcm, sr, 0, n);
        if (!sp) return null;
        var top = Math.min(9000, sr / 2 - 200);
        var hi = bandEnergy(sp, 2500, top);
        var lo = bandEnergy(sp, 150, 2500);
        var voiceLo = bandEnergy(sp, 80, 1000);
        var ratio = hi / Math.max(1e-12, hi + lo);
        var pk = peakInBand(sp, 2000, top);
        return {
            level: rms,
            hiRatio: Math.round(ratio * 100) / 100,
            fricative: ratio >= 0.55 && rms > 0.004,
            voiced: voiceLo > hi * 2,
            peakHz: Math.round(pk.hz),
            centroidHz: Math.round(centroidInBand(sp, 2000, top))
        };
    }

    window.SpeechSoundMeter = {
        sibilant: sibilant, trill: trill, frame: frame,
        envelope: envelope, speechSpan: speechSpan, spectrum: spectrum,
        peakInBand: peakInBand, centroidInBand: centroidInBand
    };
})();
